import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import { toast } from "react-toastify";
import {
  fetchLiveEventAttendees,
  deleteLiveEventAttendee,
  sendLiveEventAttendeesEmail,
} from "../../../api/dev/liveEventsAdminApi";

export const LiveEventAttendeesDialog = ({
  open,
  onClose,
  eventId,
  onAttendeeChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Email roster dispatch state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("info@mindsherpa.health");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Attendee deletion confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingAttendee, setDeletingAttendee] = useState(false);

  const loadAttendees = async () => {
    if (!eventId) return;
    try {
      setLoading(true);
      const res = await fetchLiveEventAttendees(eventId);
      if (res && res.success) {
        setEventData(res.event);
        setAttendees(res.attendees || []);
        if (res.event?.presenter_email && res.event.presenter_email.trim()) {
          setRecipientEmail(res.event.presenter_email.trim());
        } else {
          setRecipientEmail("info@mindsherpa.health");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || "Failed to load attendees for this event"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && eventId) {
      loadAttendees();
      setSearchQuery("");
      setDeleteTarget(null);
    }
  }, [open, eventId]);

  const handleDeleteAttendee = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingAttendee(true);
      await deleteLiveEventAttendee(eventId, deleteTarget.user_id);
      toast.success(
        `Removed ${deleteTarget.user_name || "User"} from event registration.`
      );
      setDeleteTarget(null);
      await loadAttendees();
      if (onAttendeeChanged) onAttendeeChanged();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || "Failed to remove attendee from event"
      );
    } finally {
      setDeletingAttendee(false);
    }
  };

  const handleSendEmail = async () => {
    const cleanEmail = recipientEmail.trim();
    if (!cleanEmail) {
      toast.error("Please enter a valid recipient email address.");
      return;
    }
    try {
      setSendingEmail(true);
      const res = await sendLiveEventAttendeesEmail(eventId, cleanEmail);
      if (res && res.success) {
        toast.success(
          `Attendee roster successfully sent to ${cleanEmail}!`
        );
        setEmailDialogOpen(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to send attendee email. Please try again."
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const filteredAttendees = attendees.filter((att) => {
    const q = searchQuery.toLowerCase();
    const name = (att.user_name || "").toLowerCase();
    const email = (att.email || "").toLowerCase();
    const uid = String(att.user_id || "");
    return name.includes(q) || email.includes(q) || uid.includes(q);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "–";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #E2E8F0",
            py: 2,
            px: 3,
            bgcolor: "#F8FAFC",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "#ECFDF5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GroupIcon sx={{ color: "#059669" }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#154C3A">
                Registered Attendees
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {eventData?.title || "Live Event"} &bull; {attendees.length} total registered
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={loadAttendees} disabled={loading} size="small" title="Refresh list">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "#F8FAFC" }}>
          {/* Event Quick Info Banner */}
          {eventData && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2.5,
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#1E293B">
                    {eventData.title}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748B" }}>
                      <PersonIcon sx={{ fontSize: 15, color: "#059669" }} /> {eventData.presenter}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748B" }}>
                      <EventIcon sx={{ fontSize: 15, color: "#059669" }} /> {formatDate(eventData.event_date)}
                    </Typography>
                    {eventData.stream_link && (
                      <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748B" }}>
                        <VideoCameraFrontIcon sx={{ fontSize: 15, color: "#059669" }} />
                        <Link
                          href={eventData.stream_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: "#0284C7", textDecoration: "underline", fontWeight: 600 }}
                        >
                          Meeting Link
                        </Link>
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Chip
                  label={`${attendees.length} / ${eventData.capacity} Seats Filled`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: attendees.length >= eventData.capacity ? "#FEF2F2" : "#ECFDF5",
                    color: attendees.length >= eventData.capacity ? "#DC2626" : "#059669",
                    border: "1px solid",
                    borderColor: attendees.length >= eventData.capacity ? "#FCA5A5" : "#A7F3D0",
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setEmailDialogOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    bgcolor: "#154C3A",
                    "&:hover": { bgcolor: "#0D3327" },
                  }}
                >
                  Send Attendee List
                </Button>
              </Box>
            </Paper>
          )}

          {/* Search bar */}
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search attendees by name, email, or user ID..."
              fullWidth
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, bgcolor: "#FFFFFF" },
              }}
            />
          </Box>

          {/* Attendees Table */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={36} sx={{ color: "#154C3A" }} />
            </Box>
          ) : filteredAttendees.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: 2.5,
                border: "1px dashed #CBD5E1",
                bgcolor: "#FFFFFF",
              }}
            >
              <PersonIcon sx={{ fontSize: 44, color: "#CBD5E1", mb: 1 }} />
              <Typography variant="body1" fontWeight={600} color="#64748B">
                {searchQuery ? "No matching attendees found" : "No attendees registered yet"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {searchQuery
                  ? "Try searching with a different name or email"
                  : "Registered users will appear here once they enroll in this session"}
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: "1px solid #E2E8F0",
                maxHeight: 380,
                overflowY: "auto",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { bgcolor: "#F1F5F9", fontWeight: 700, color: "#475569" } }}>
                    <TableCell width={70}>User ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Registered At</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" width={80}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttendees.map((att) => (
                    <TableRow
                      key={att.registration_id || `${att.event_id}-${att.user_id}`}
                      hover
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#64748B" }}>
                        #{att.user_id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#1E293B" }}>
                        {att.user_name || "User"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component="a"
                          href={`mailto:${att.email}`}
                          sx={{
                            color: "#0284C7",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {att.email || "–"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: "#64748B", fontSize: "0.8rem" }}>
                        {formatDate(att.registered_at)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={att.status || "Registered"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: att.status === "Attended" ? "#ECFDF5" : "#EFF6FF",
                            color: att.status === "Attended" ? "#059669" : "#2563EB",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove attendee from event">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(att)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #E2E8F0", bgcolor: "#F8FAFC" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: "#64748B",
              borderColor: "#CBD5E1",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog: Delete Attendee */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deletingAttendee ? undefined : setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#DC2626" }}>
          Remove Attendee?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#475569">
            Are you sure you want to remove{" "}
            <strong>{deleteTarget?.user_name || deleteTarget?.email || "this user"}</strong>{" "}
            from this live event registration? Their seat will be freed immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC", gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deletingAttendee}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAttendee}
            disabled={deletingAttendee}
            variant="contained"
            color="error"
            startIcon={deletingAttendee ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {deletingAttendee ? "Removing..." : "Remove Attendee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Send Attendee List to Email */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => (sendingEmail ? undefined : setEmailDialogOpen(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#154C3A" }}>
          Send Attendee Roster Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#475569" sx={{ mb: 2.5 }}>
            Send the complete registered attendee list and event summary table to the email address below:
          </Typography>
          <TextField
            label="Recipient Email Address"
            placeholder={eventData?.presenter_email || "info@mindsherpa.health"}
            fullWidth
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ color: "#64748B" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 },
            }}
            helperText={
              eventData?.presenter_email
                ? `Default: Presenter email (${eventData.presenter_email}). You can edit or enter any recipient.`
                : "Default: info@mindsherpa.health. You can enter any custom email address."
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#F8FAFC", gap: 1 }}>
          <Button
            onClick={() => setEmailDialogOpen(false)}
            disabled={sendingEmail}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            variant="contained"
            startIcon={sendingEmail ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#154C3A",
              "&:hover": { bgcolor: "#0D3327" },
            }}
          >
            {sendingEmail ? "Sending..." : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
